import React from 'react';
import { DateField } from '../../dropdown';
import { JobWithNotes } from '../workItemTypes';
import { AppointmentsHandlers } from '../hooks/useAppointments';
import { getAvailableTechnicians } from '../utils/workItemUtils';
import { AppointmentType } from '@techgitter/contracts';

const APPOINTMENT_TYPES: AppointmentType[] = ['Instore', 'Onsite', 'Phone Call', 'Remote Support'];

export interface AppointmentFormProps {
  job: JobWithNotes;
  jobs: JobWithNotes[];
  appointmentsHandlers: AppointmentsHandlers;
  isVisible: boolean;
  onClose: () => void;
}

export const AppointmentForm: React.FC<AppointmentFormProps> = ({
  job,
  jobs,
  appointmentsHandlers,
  isVisible,
  onClose
}) => {
  const {
    appointmentDate,
    appointmentTime,
    appointmentTask,
    appointmentOwner,
    appointmentType,
    editingAppointmentId,
    setAppointmentDate,
    setAppointmentTime,
    setAppointmentTask,
    setAppointmentOwner,
    setAppointmentType,
    handleAddAppointment,
    handleUpdateAppointment
  } = appointmentsHandlers;

  const availableTechnicians = getAvailableTechnicians(jobs, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editingAppointmentId) {
      handleUpdateAppointment(job);
    } else {
      handleAddAppointment(job);
    }
    
    onClose();
  };

  const handleCancel = () => {
    setAppointmentDate('');
    setAppointmentTime('');
    setAppointmentTask('');
    setAppointmentOwner('');
    onClose();
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="appointment-form-overlay">
      <div className="appointment-form">
        <div className="form-header">
          <h3>{editingAppointmentId ? 'Edit Appointment' : 'Schedule Appointment'}</h3>
          <button type="button" className="close-btn" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <DateField
              label="Date"
              value={appointmentDate || null}
              onChange={(iso) => setAppointmentDate(iso ?? '')}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="appointment-time">Time:</label>
            <input
              id="appointment-time"
              type="time"
              value={appointmentTime}
              onChange={(e) => setAppointmentTime(e.target.value)}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="appointment-type">Type:</label>
            <select
              id="appointment-type"
              value={appointmentType}
              onChange={(e) => setAppointmentType(e.target.value as AppointmentType)}
              required
            >
              {APPOINTMENT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="appointment-task">Task:</label>
            <textarea
              id="appointment-task"
              value={appointmentTask}
              onChange={(e) => setAppointmentTask(e.target.value)}
              placeholder="Describe the appointment task..."
              rows={3}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="appointment-owner">Assigned Technician:</label>
            <select
              id="appointment-owner"
              value={appointmentOwner}
              onChange={(e) => setAppointmentOwner(e.target.value)}
              required
            >
              <option value="">Select a technician</option>
              {availableTechnicians.map((tech) => (
                <option key={tech} value={tech}>
                  {tech}
                </option>
              ))}
            </select>
          </div>
          
          <div className="form-actions">
            <button type="button" onClick={handleCancel} className="btn-secondary">
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-primary"
              disabled={!appointmentDate || !appointmentTime || !appointmentTask}
            >
              {editingAppointmentId ? 'Update Appointment' : 'Schedule Appointment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};